/**
 * Client Portal — Run Assessment page.
 *
 * Shows approved assessment domains (from EM-approved requests) plus any
 * saved in-progress drafts.  Clients can start a new assessment for an
 * approved domain or resume a saved draft.
 *
 * Domains only appear here after the EM approves the client's request.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Box, Paper, Grid, Card, CardContent, CardActionArea,
  CircularProgress, Alert, Chip, Button, LinearProgress, Divider,
  Tooltip, IconButton,
} from '@mui/material';
import {
  PlayArrow, Restore, Security, SmartToy, Build, CloudQueue,
  Business, Architecture, Timer, Delete, CheckCircle,
} from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';
import PortalTooltip from '../../../components/PortalTooltip';
import AssessmentFlow from '../../../components/AssessmentFlow';

/* ── Helpers ──────────────────────────────────────────────────────── */

interface ApprovedDomain {
  request_id: string;
  category: string;
  domain: string;
  priority: string;
  approved_at: string | null;
  has_draft: boolean;
  draft_id: string | null;
  draft_progress: string | null;
  draft_expires_at: string | null;
}

interface DraftItem {
  id: string;
  category: string;
  domain: string;
  request_id: string | null;
  answered_count: number;
  total_questions: number;
  current_step: number;
  created_at: string | null;
  updated_at: string | null;
  expires_at: string | null;
}

const getCategoryIcon = (category: string) => {
  const sx = { fontSize: 36 };
  switch (category) {
    case 'Security & Compliance': return <Security sx={{ ...sx, color: '#f44336' }} />;
    case 'AI & Analytics': return <SmartToy sx={{ ...sx, color: '#9c27b0' }} />;
    case 'Development & Operations': return <Build sx={{ ...sx, color: '#ff9800' }} />;
    case 'Cloud Strategy & Adoption': return <CloudQueue sx={{ ...sx, color: '#2196f3' }} />;
    case 'Productivity & Collaboration': return <Business sx={{ ...sx, color: '#4caf50' }} />;
    case 'Baseline Architecture': return <Architecture sx={{ ...sx, color: '#795548' }} />;
    default: return <CloudQueue sx={{ ...sx, color: 'primary.main' }} />;
  }
};

const PRIORITY_COLORS: Record<string, 'default' | 'warning' | 'error' | 'info'> = {
  low: 'default', normal: 'info', high: 'warning', urgent: 'error',
};

/* ── Component ────────────────────────────────────────────────────── */

const RunAssessment: React.FC = () => {
  const [approvedDomains, setApprovedDomains] = useState<ApprovedDomain[]>([]);
  const [drafts, setDrafts] = useState<DraftItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Active assessment state  
  const [activeAssessment, setActiveAssessment] = useState<{
    category: string;
    domain: string;
    requestId?: string;
    draftId?: string;
    savedAnswers?: Record<string, { selectedOptions: number[]; comment: string }>;
    savedOrgInfo?: any;
    savedStep?: number;
  } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [domainsRes, draftsRes] = await Promise.all([
        authFetch('/api/client/approved-domains'),
        authFetch('/api/client/assessment-drafts'),
      ]);

      if (!domainsRes.ok) {
        const err = await domainsRes.json().catch(() => ({}));
        throw new Error(err.error || `Failed to load approved domains: ${domainsRes.status}`);
      }
      if (!draftsRes.ok) {
        const err = await draftsRes.json().catch(() => ({}));
        throw new Error(err.error || `Failed to load drafts: ${draftsRes.status}`);
      }

      const domainsData = await domainsRes.json();
      const draftsData = await draftsRes.json();

      if (domainsData.success) setApprovedDomains(domainsData.domains);
      if (draftsData.success) setDrafts(draftsData.drafts);
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLastRefreshed(new Date());
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /** Start a new assessment for an approved domain */
  const handleStartAssessment = (ad: ApprovedDomain) => {
    setActiveAssessment({
      category: ad.category,
      domain: ad.domain,
      requestId: ad.request_id,
    });
  };

  /** Resume a saved draft */
  const handleResumeDraft = async (draft: DraftItem) => {
    try {
      const res = await authFetch(`/api/client/assessment-drafts/${draft.id}`);
      const data = await res.json();
      if (!data.success) {
        setError(data.error || 'Failed to load draft');
        return;
      }
      setActiveAssessment({
        category: data.draft.category,
        domain: data.draft.domain,
        requestId: data.draft.request_id,
        draftId: data.draft.id,
        savedAnswers: data.draft.answers,
        savedOrgInfo: data.draft.organization_info,
        savedStep: data.draft.current_step,
      });
    } catch {
      setError('Failed to load draft');
    }
  };

  /** Delete an expired or unwanted draft */
  const handleDeleteDraft = async (draftId: string) => {
    try {
      await authFetch(`/api/client/assessment-drafts/${draftId}`, { method: 'DELETE' });
      setDrafts((prev) => prev.filter((d) => d.id !== draftId));
    } catch { /* best-effort */ }
  };

  /** Dismiss a concluded approved domain */
  const handleDismissDomain = async (requestId: string) => {
    try {
      const res = await authFetch(`/api/client/approved-domains/${requestId}/complete`, { method: 'PUT' });
      if (res.ok) {
        setApprovedDomains((prev) => prev.filter((ad) => ad.request_id !== requestId));
      }
    } catch { /* best-effort */ }
  };

  /** Back from assessment flow to selection screen */
  const handleBack = () => {
    setActiveAssessment(null);
    fetchData(); // Refresh lists
  };

  // ── If an assessment is active, show the flow ──────────
  if (activeAssessment) {
    return (
      <Box>
        <AssessmentFlow
          category={activeAssessment.category}
          domain={activeAssessment.domain}
          onBack={handleBack}
          draftId={activeAssessment.draftId}
          savedAnswers={activeAssessment.savedAnswers}
          savedOrganizationInfo={activeAssessment.savedOrgInfo}
          savedStep={activeAssessment.savedStep}
          requestId={activeAssessment.requestId}
          hideExcelExport
        />
      </Box>
    );
  }

  // ── Loading / Error states ────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Drafts that don't have a matching approved domain (standalone)
  const standaloneDrafts = drafts.filter(
    (d) => !approvedDomains.some((ad) => ad.draft_id === d.id),
  );

  return (
    <Box>
      <PortalTooltip tooltipKey="client.page.run_assessment" showIcon>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Run Assessment
        </Typography>
          <RefreshButton onRefresh={fetchData} loading={loading} lastRefreshed={lastRefreshed} />
        </Box>
      </PortalTooltip>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Start an assessment for an approved domain or resume a saved draft.
        Request new assessments from the Assessment Requests page.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── In-Progress Drafts ── */}
      {drafts.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
            <Restore sx={{ verticalAlign: 'middle', mr: 1 }} />
            In-Progress Assessments
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {drafts.map((draft) => {
              const progress = draft.total_questions > 0
                ? (draft.answered_count / draft.total_questions) * 100 : 0;
              const expiresDate = draft.expires_at ? new Date(draft.expires_at) : null;
              const daysLeft = expiresDate
                ? Math.max(0, Math.ceil((expiresDate.getTime() - Date.now()) / 86400000))
                : null;

              return (
                <Grid item xs={12} md={6} lg={4} key={draft.id}>
                  <Card sx={{
                    border: '2px solid #ff9800',
                    position: 'relative',
                    '&:hover': { boxShadow: 4 },
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {getCategoryIcon(draft.category)}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {draft.domain}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {draft.category}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {draft.answered_count} / {draft.total_questions} questions answered
                        </Typography>
                        <LinearProgress variant="determinate" value={progress}
                          sx={{ height: 6, borderRadius: 3 }} />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                        <Chip label="In Progress" size="small" color="warning" />
                        {daysLeft !== null && (
                          <Chip
                            icon={<Timer sx={{ fontSize: 14 }} />}
                            label={`${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
                            size="small"
                            color={daysLeft <= 1 ? 'error' : 'default'}
                          />
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PlayArrow />}
                          onClick={() => handleResumeDraft(draft)}
                          sx={{ bgcolor: '#00338D', flex: 1 }}
                        >
                          Resume
                        </Button>
                        <Tooltip title="Delete draft">
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => handleDeleteDraft(draft.id)}
                            sx={{ minWidth: 36, px: 1 }}
                          >
                            <Delete fontSize="small" />
                          </Button>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      {/* ── Approved Domains ── */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        <PlayArrow sx={{ verticalAlign: 'middle', mr: 1 }} />
        Approved Assessment Domains
      </Typography>

      {approvedDomains.length === 0 && standaloneDrafts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No approved assessment domains yet. Submit a request from the{' '}
            <strong>Assessment Requests</strong> page and wait for EM approval.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {approvedDomains.map((ad) => (
            <Grid item xs={12} md={6} lg={4} key={ad.request_id}>
              <Card sx={{
                height: '100%',
                border: ad.has_draft ? '2px solid #ff9800' : '1px solid #e0e0e0',
                '&:hover': { boxShadow: 4 },
                position: 'relative',
              }}>
                {/* Dismiss button — marks concluded domain as completed */}
                <Tooltip title="Dismiss completed domain">
                  <IconButton
                    size="small"
                    onClick={() => handleDismissDomain(ad.request_id)}
                    sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }}
                  >
                    <CheckCircle fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
                <CardActionArea
                  onClick={() => ad.has_draft
                    ? handleResumeDraft(drafts.find((d) => d.id === ad.draft_id) || { id: ad.draft_id!, category: ad.category, domain: ad.domain, request_id: ad.request_id, answered_count: 0, total_questions: 0, current_step: 2, created_at: null, updated_at: null, expires_at: ad.draft_expires_at })
                    : handleStartAssessment(ad)
                  }
                  sx={{ height: '100%' }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      {getCategoryIcon(ad.category)}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                          {ad.domain}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ad.category}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip label="Approved" size="small" color="success" />
                      <Chip label={ad.priority} size="small" color={PRIORITY_COLORS[ad.priority] || 'default'} />
                      {ad.has_draft && (
                        <Chip label={`Draft: ${ad.draft_progress}`} size="small" color="warning" />
                      )}
                    </Box>

                    {ad.approved_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Approved: {new Date(ad.approved_at).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default RunAssessment;
